import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { defaultSettings, getStoredUserSettings, saveUserSettings, applyUserTheme } from '../utils/userSettings';

const Settings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState(defaultSettings);
  const [saved, setSaved] = useState(false);
  const isSuperAdmin = user?.isSuperAdmin;

  useEffect(() => {
    const nextSettings = getStoredUserSettings(user?.id);
    setSettings(nextSettings);
    applyUserTheme(nextSettings);
  }, [user]);

  const handleToggle = (key) => {
    setSettings(prev => {
      const nextSettings = { ...prev, [key]: !prev[key] };
      if (key === 'darkMode') {
        applyUserTheme(nextSettings);
      }
      return nextSettings;
    });
    setSaved(false);
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSave = () => {
    saveUserSettings(user?.id, settings);
    applyUserTheme(settings);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <Layout>
      <div className="px-4 py-6 sm:px-0">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Settings</h1>

        <div className="bg-white shadow rounded-lg divide-y divide-gray-200">
          <div className="px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900">Preferences</h2>
            <p className="text-sm text-gray-500">Customize your experience</p>
          </div>

          {/* Dark Mode */}
          <div className="px-6 py-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Dark Mode</p>
              <p className="text-xs text-gray-500">Use dark theme across the app</p>
            </div>
            <button
              onClick={() => handleToggle('darkMode')}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                settings.darkMode ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                settings.darkMode ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </button>
          </div>

          {/* Compact View */}
          <div className="px-6 py-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Compact View</p>
              <p className="text-xs text-gray-500">Reduce spacing in tables and lists</p>
            </div>
            <button
              onClick={() => handleToggle('compactView')}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                settings.compactView ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                settings.compactView ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </button>
          </div>

          {/* Show Email */}
          <div className="px-6 py-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Show Email in List</p>
              <p className="text-xs text-gray-500">Display email addresses in the applicant list</p>
            </div>
            <button
              onClick={() => handleToggle('showEmail')}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                settings.showEmail ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                settings.showEmail ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </button>
          </div>

          {/* Show Phone 2 */}
          <div className="px-6 py-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Show Phone 2 in List</p>
              <p className="text-xs text-gray-500">Display secondary phone numbers in the applicant list</p>
            </div>
            <button
              onClick={() => handleToggle('showPhone2')}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                settings.showPhone2 ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                settings.showPhone2 ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </button>
          </div>

          {/* Page Size */}
          <div className="px-6 py-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Default Page Size</p>
              <p className="text-xs text-gray-500">Number of records per page</p>
            </div>
            <select
              value={settings.pageSize}
              onChange={(e) => handleChange('pageSize', e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
            </select>
          </div>

          {/* Save */}
          <div className="px-6 py-4 flex justify-end items-center space-x-3">
            {saved && <span className="text-sm text-green-600">Settings saved!</span>}
            <button
              onClick={handleSave}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              Save Settings
            </button>
          </div>
        </div>

        {isSuperAdmin && (
          <div className="mt-6 rounded-lg bg-white shadow">
            <div className="px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Admin Tools</h2>
              <p className="text-sm text-gray-500">Manage system-wide configuration.</p>
            </div>
            <div className="flex flex-wrap gap-3 px-6 pb-6">
              <Link
                to="/settings/email"
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                Email Settings
              </Link>
              <Link
                to="/settings/telegram-bot"
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                Configure Telegram Bot
              </Link>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Settings;
